import { err, ok } from 'neverthrow';

import {
  getApiMapping,
  getDevicePlatform,
  getMetadata,
  isLoggingToSplunkEnabled,
  logLocal,
  shouldReportError,
} from '@rover/rsdk/src/modules/Logging/internals';

import MessageQueue, { Message, MessageQueueBatchResult } from '../../utils/queue';
import {
  QueueSendConfigurationError,
  QueueSendHttpError,
  QueueSendNetworkError,
} from '../../utils/queue/QueueSendError';
import captureError from '../ErrorReporting/captureError';
import ObservabilityMetadata from '../ObservabilityMetadata';

import { SPLUNK_API_KEYS } from './config';
import { LogSeverity, SPLUNK_URL } from './constants';
import LoggingMessage from './LoggingMessage';
import { LoggingMetadata } from './types';
import { getApiEnvironmentName } from './utils';

export type LoggingMessageBatch = Array<Message<LoggingMessage>>;

export default class Logger {
  private static queue: MessageQueue<LoggingMessage> = new MessageQueue(Logger.sendBatch);

  static platform(): string {
    return getDevicePlatform();
  }

  static async host(): Promise<string | null> {
    return getApiMapping();
  }

  static async splunkIndex(): Promise<string | null> {
    const currentPlatform: string = Logger.platform().toLowerCase();
    const apiEnvironmentName = await getApiEnvironmentName(currentPlatform);

    return apiEnvironmentName ? `${currentPlatform}_${apiEnvironmentName}` : null;
  }

  static async splunkToken(): Promise<string | null> {
    const index = await Logger.splunkIndex();
    return index ? SPLUNK_API_KEYS[index] : null;
  }

  static async platformMetadata(): Promise<Partial<LoggingMetadata>> {
    return getMetadata();
  }

  static info(message: string, metadata: Record<string, string> = {}): void {
    Logger.log(message, metadata, LogSeverity.INFO);
  }

  static debug(message: string, metadata: Record<string, string> = {}): void {
    Logger.log(message, metadata, LogSeverity.DEBUG);
  }

  static warn(message: string, metadata: Record<string, string> = {}): void {
    Logger.log(message, metadata, LogSeverity.WARN);
  }

  static error(error: Error, metadata: Record<string, string> = {}): void {
    Logger.log(error.message, metadata, LogSeverity.ERROR, error);
    if (shouldReportError(error)) {
      captureError(error);
    }
  }

  private static async sendBatch(messageBatch: LoggingMessageBatch): MessageQueueBatchResult {
    const [host, splunkIndex, splunkToken] = await Promise.all([
      Logger.host(),
      Logger.splunkIndex(),
      Logger.splunkToken(),
    ]);
    const logPayload = Logger.makePayload(messageBatch, host, splunkIndex);

    // short-circuit if the token is missing altogether
    if (!splunkToken) {
      return err(new QueueSendConfigurationError('No Splunk token configured'));
    }

    try {
      const postResult = await fetch(SPLUNK_URL, {
        method: 'POST',
        headers: {
          Authorization: `Splunk ${splunkToken}`,
        },
        body: logPayload,
      });

      if (postResult.ok) {
        // return ok() with the message uuids so the queue marks them as done
        return ok(messageBatch.map((message) => message.id));
      }

      const rawBody = await postResult.text();
      let bodyJson: Partial<{ text: string; code: number }> | undefined;

      try {
        bodyJson = rawBody ? JSON.parse(rawBody) : undefined;
      } catch (parseError) {
        if (__DEV__) {
          // show exactly what failed during debugging
          // eslint-disable-next-line no-console
          console.warn('Failed to parse Splunk error body', parseError, rawBody);
        }
      }

      // build a readable error string from Splunk’s JSON response
      let bodyDetail: string | undefined;
      if (bodyJson?.text) {
        bodyDetail =
          bodyJson.code !== undefined ? `${bodyJson.text} (code ${bodyJson.code})` : bodyJson.text;
      }

      if (postResult.status === 403) {
        return err(
          new QueueSendConfigurationError( // this is NOT a retryable error
            bodyDetail ?? 'Auth error sending logs to Splunk, is the token being set correctly?'
          )
        );
      }

      // general http error handling, this is a retryable error
      return err(
        new QueueSendHttpError(
          `HTTP error sending logs to Splunk. Status: ${postResult.status} Message: ${
            bodyDetail ?? rawBody
          }`
        )
      );
    } catch (e) {
      // fetch throws for improper fetch() calls, aborted network requests, and network (not server) errors
      if (
        e instanceof TypeError &&
        e.message === 'Failed to fetch' &&
        typeof navigator !== 'undefined' && // Checks that user is in web environment
        navigator.onLine
      ) {
        // If the client is online but the request failed, it was likely blocked.
        return ok(messageBatch.map((message) => message.id));
      }
      const message = e instanceof Error ? e.message : 'Unknown error';
      return err(
        new QueueSendNetworkError( // this is a retryable error
          `Network error sending logs to Splunk: ${message}`
        )
      );
    }
  }

  private static makePayload(
    messages: LoggingMessageBatch,
    host: string | null,
    index: string | null
  ): string {
    return messages
      .map((message) => {
        return JSON.stringify({
          event: {
            message: message.content.message,
            severity: message.content.severity,
            metadata: message.content.metadata,
            error: message.content.error,
          },
          host,
          index,
          source: Logger.platform(),
          time: Date.now().toString(),
        });
      })
      .join('\n');
  }

  private static async log(
    message: string,
    metadata: Record<string, string>,
    severity: LogSeverity,
    error?: Error
  ): Promise<void> {
    const observabilityMetadata = ObservabilityMetadata.data();
    const platformMetadata = await Logger.platformMetadata();

    const mergedMetadata = {
      ...metadata,
      ...observabilityMetadata,
      ...platformMetadata,
    };

    // log to the console in every build and deploy env,
    // no reason not to since all this info will be going over the network anyway
    logLocal(message, mergedMetadata, severity, error);

    // don't log to splunk in places it doesn't work
    if (!(await isLoggingToSplunkEnabled())) return;

    if (!this.queue.isStarted()) {
      this.queue.start();
    }

    const log = new LoggingMessage({
      message,
      severity,
      metadata: mergedMetadata,
      error,
    });
    this.queue.push(log);
  }
}
